import React, { useEffect, useState, useMemo } from 'react';
import API_URL from '../settings';
import {
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

import { PeopleAlt, EventAvailable, Dangerous } from '@mui/icons-material';

/* ---------- Utils ---------- */
const todayStr = () => new Date().toISOString().split('T')[0];

const isOverDays = (date, days) => {
  if (!date) return true;
  const d = new Date(date);
  const diff = (new Date() - d) / (1000 * 60 * 60 * 24);
  return diff > days;
};

/* ---------- Small Components ---------- */

const KpiCard = ({ title, value, icon }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderRadius: 3,
      boxShadow: 3,
    }}
  >
    <Box sx={{ mr: 2, color: 'primary.main' }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="h4" fontWeight="bold">
        {value}
      </Typography>
    </Box>
  </Paper>
);

/* ---------- Widgets ---------- */

const PatientsToFollowUp = ({ patients }) => {
  const list = patients.filter(p => {
    const hs = p.health_summary;
    return hs?.status === 'RISK' && isOverDays(hs.last_checkup, 30);
  });

  return (
    <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
      <Typography variant="h6">ผู้สูงอายุที่ต้องติดตาม</Typography>
      <Typography variant="caption" color="text.secondary">
        (สถานะเสี่ยง และไม่ได้ตรวจใน 30 วัน)
      </Typography>

      {list.length > 0 ? (
        <List dense>
          {list.map(p => (
            <ListItem key={p.id} disablePadding sx={{ mb: 1 }}>
              <ListItemText
                primary={`${p.first_name} ${p.last_name}`}
                secondary={`ตรวจล่าสุด: ${p.health_summary?.last_checkup || '-'}`}
              />
              <Chip label="เสี่ยง" color="error" size="small" />
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            ไม่มีผู้ที่ต้องติดตาม
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const TodayVisits = ({ visits, patients }) => {
  const today = todayStr();

  const todayVisits = visits.filter(v => v.visit_date === today);

  const patientMap = useMemo(() => {
    const map = {};
    patients.forEach(p => {
      map[p.id] = `${p.first_name} ${p.last_name}`;
    });
    return map;
  }, [patients]);

  return (
    <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
      <Typography variant="h6">รายการที่ต้องเข้าเยี่ยมวันนี้</Typography>
      <Typography variant="caption" color="text.secondary">
        ({today})
      </Typography>

      {todayVisits.length > 0 ? (
        <List dense>
          {todayVisits.map(v => (
            <ListItem key={v.visit_id} disablePadding sx={{ mb: 1 }}>
              <ListItemText
                primary={patientMap[v.patient_id] || 'ไม่พบชื่อผู้สูงอายุ'}
                secondary={
                  <>
                    ⏰ {v.visit_time || '-'} <br />
                    📍 {v.location || '-'} <br />
                    🩺 {v.service || '-'}
                  </>
                }
              />
              <Chip label="วันนี้" color="primary" size="small" />
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            ไม่มีรายการเข้าเยี่ยมวันนี้
          </Typography>
        </Box>
      )}
    </Paper>
  );
};


const DiseaseDistributionChart = ({ patients }) => {
  const theme = useTheme();
  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  const data = useMemo(() => {
    const acc = {};
    patients.forEach(p => {
      if (p.chronic_disease_note && p.chronic_disease_note !== "No" && p.chronic_disease_note !== "**") {
        const diseases = p.chronic_disease_note.split(',').map(d => d.trim()).filter(d => d);
        diseases.forEach(d => {
          acc[d] = (acc[d] || 0) + 1;
        });
      }
    });
    return Object.entries(acc).map(([name, value]) => ({ name, value }));
  }, [patients]);

  return (
    <Paper sx={{ p: 3, height: 400 }}>
      <Typography variant="h6">สถิติโรคประจำตัวในชุมชน</Typography>

      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height="85%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" outerRadius="80%" label>
              {data.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ textAlign: 'center', mt: 10 }}>
          <Typography variant="body2" color="text.secondary">
            ไม่พบข้อมูลโรค
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const BPTrendChart = ({ records }) => {
  const theme = useTheme();

  const data = useMemo(() => {
    const map = {};
    records.forEach(r => {
      if (!r.blood_pressure) return;
      const month = r.checkup_date?.slice(0, 7);
      if (!month) return;

      if (!map[month]) map[month] = { month, s: 0, d: 0, c: 0 };
      map[month].s += r.blood_pressure.systolic;
      map[month].d += r.blood_pressure.diastolic;
      map[month].c += 1;
    });

    return Object.values(map)
      .map(m => ({
        month: m.month,
        Systolic: Math.round(m.s / m.c),
        Diastolic: Math.round(m.d / m.c),
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [records]);

  return (
    <Paper sx={{ p: 3, height: 400 }}>
      <Typography variant="h6">แนวโน้มความดันโลหิตเฉลี่ย</Typography>

      <ResponsiveContainer width="100%" height="85%">
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="Systolic"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="Diastolic"
            stroke={theme.palette.secondary.main}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

/* ---------- Main Dashboard ---------- */

function Dashboard() {
  const { user } = useAuth();

  const [patients, setPatients] = useState([]);
  const [records, setRecords] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const patientsUrl =
          user.role === 'VOLUNTEER'
            ? `${API_URL}/patients?volunteer_id=${user.id}`
            : `${API_URL}/patients?hospital_id=${user.hospital_id}`;

        const [p, r, v] = await Promise.all([
          fetch(patientsUrl).then(res => res.json()),
          fetch(`${API_URL}/health_records`).then(res => res.json()),
          fetch(`${API_URL}/visits`).then(res => res.json()),
        ]);

        setPatients(p);
        setRecords(r);
        setVisits(v);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  /* ---------- KPI ---------- */

  const riskCount = patients.filter(
    p => p.health_summary?.status === 'RISK'
  ).length;

  const todayVisitCount = visits.filter(
    v => v.visit_date === todayStr()
  ).length;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        ภาพรวมสุขภาพชุมชน
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <KpiCard
            title="ผู้สูงอายุในระบบ"
            value={patients.length}
            icon={<PeopleAlt fontSize="large" />}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <KpiCard
            title="กลุ่มเสี่ยง"
            value={riskCount}
            icon={<Dangerous fontSize="large" />}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <KpiCard
            title="การเยี่ยม / ตรวจวันนี้"
            value={todayVisitCount}
            icon={<EventAvailable fontSize="large" />}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <BPTrendChart records={records} />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <PatientsToFollowUp patients={patients} />
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <DiseaseDistributionChart patients={patients} />
        </Grid>


        <Grid size={{ xs: 12, md: 4 }}>
          <TodayVisits visits={visits} patients={patients} />
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
